<template>
    <div
        :class="['group-heading-container', { dark: theme == THEMES.DARK }, { active: showMenu }]"
        v-if="sidebarLinkGroupFitered && sidebarLinkGroupFitered.length"
    >
        <button class="group-heading" type="button" @click="visitLink(sidebarLinkGroup)">
            <div class="group-heading-content">
                <div class="group-icon" v-if="sidebarLinkGroup.icon">
                    <i class="material-icons">{{ sidebarLinkGroup.icon }}</i>
                </div>
                <div class="group-title">{{ sidebarLinkGroup.title }}</div>
                <div class="group-chevron">
                    <i class="material-icons">{{ showMenu ? 'expand_more' : 'chevron_right' }}</i>
                </div>
            </div>
        </button>
        <transition name="slide">
            <div class="group-items" v-if="showMenu">
                <router-link
                    v-for="(sidebarLink, i) of sidebarLinkGroupFitered"
                    :key="i"
                    :to="sidebarLink.link"
                    :class="['sidebar-item', { active: isLinkActive(sidebarLink) }]"
                    @click.native="scrollTopFirst"
                    v-if="!sidebarLink.forceReload"
                >
                    <div class="sidebar-item-content">
                        <div class="item-dot"></div>
                        <div class="item-title">{{ sidebarLink.title }}</div>
                    </div>
                </router-link>
                <a
                    v-for="(sidebarLink, i) of sidebarLinkGroupFitered.filter(l => l.forceReload)"
                    :key="`reload-${i}`"
                    :href="sidebarLink.link"
                    :class="['sidebar-item', { active: isLinkActive(sidebarLink) }]"
                >
                    <div class="sidebar-item-content">
                        <div class="item-dot"></div>
                        <div class="item-title">{{ sidebarLink.title }}</div>
                    </div>
                </a>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission } from "../../store/permission"
import { scrollPageToTop } from "../../utils/scroll-container"

export default {
    props: {
        sidebarLinkGroup: { type: Object, default: () => ({}) },
        sm: { type: Boolean, default: () => false },
    },
    data() {
        return { showMenu: false }
    },
    mounted() {
        this.updateLinkActive()
    },
    methods: {
        updateLinkActive() {
            for (let sidebarLink of this.sidebarLinkGroup.data) {
                if (this.isLinkActive(sidebarLink)) {
                    this.showMenu = true
                }
            }
        },
        isLinkActive(sidebarLink) {
            if (!sidebarLink?.link) return false
            if (this.$route.path === sidebarLink.link) return true
            return Boolean(sidebarLink.matchPrefix && this.$route.path.startsWith(`${sidebarLink.link}/`))
        },
        scrollTopFirst() {
            scrollPageToTop()
        },
        visitLink(sidebarLinkGroup) {
            this.showMenu = !this.showMenu
            if (sidebarLinkGroup?.isIndexUrl) {
                this.$router.push({
                    name: sidebarLinkGroup.title,
                })
            }
        },
    },
    watch: {
        $route(e) {
            this.updateLinkActive()
        },
    },
    computed: {
        ...mapState("auth", ["user"]),
        ...mapState("misc", ["theme", "THEMES"]),

        sidebarLinkGroupFitered() {
            // filter by permissions
            const userPermissionIds = this.user?.permissionIds || []
            const filteredLinks = this.sidebarLinkGroup.data.filter(
                (sidebarLink) => {
                    // A link may decide visibility itself (for example manager read access).
                    if (typeof sidebarLink.visibleFor === "function") {
                        return Boolean(sidebarLink.visibleFor(this.user))
                    }
                    if (sidebarLink.anyPermissions) return sidebarLink.anyPermissions.some(permissionId => checkHasPermission({
                        isAdmin: Boolean(this.user?.isAdmin), permissionId, userPermissionIds,
                    }))
                    const permissions = sidebarLink.permissions || [sidebarLink.permission]
                    return permissions.every(permissionId => checkHasPermission({
                        isAdmin: Boolean(this.user?.isAdmin),
                        permissionId,
                        userPermissionIds,
                    }))
                }
            )
            return filteredLinks
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.group-heading-container {
    margin-bottom: 4px;
}

.group-heading {
    display: block;
    width: calc(100% - 16px);
    padding: 10px 20px;
    cursor: pointer;
    border: 0;
    border-radius: 8px;
    margin: 0 8px;
    background: transparent;
    font: inherit;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
    
    .group-heading-content {
        display: flex;
        align-items: center;
        
        .group-icon {
            margin-right: 12px;
            color: rgba(255, 255, 255, 0.9);
            width: 20px; /* Fixed width for consistent alignment */
            flex-shrink: 0;
            
            i {
                font-size: 20px;
            }
        }
        
        .group-title {
            flex: 1;
            font-size: 14px;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.9);
            text-align: left;
        }
        
        .group-chevron {
            color: rgba(255, 255, 255, 0.6);
            transition: transform 0.3s ease;
            
            i {
                font-size: 18px;
            }
        }
    }
}

.group-heading-container.active {
    .group-heading {
        background-color: rgba(255, 255, 255, 0.15);
    }
}

.group-items {
    padding: 5px 0;
}

.sidebar-item {
    display: block;
    padding: 8px 20px 8px 50px;
    text-decoration: none;
    border-radius: 8px;
    margin: 2px 8px;
    transition: all 0.2s ease;
    
    .sidebar-item-content {
        display: flex;
        align-items: center;
        gap: 10px;
        
        .item-dot {
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background-color: rgba(255, 255, 255, 0.6);
        }
        
        .item-title {
            font-size: 13px;
            color: rgba(255, 255, 255, 0.7);
            font-weight: 400;
        }
    }
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
    
    &.active {
        background-color: $secondaryColor;
        
        .sidebar-item-content {
            .item-dot {
                background-color: white;
            }
            
            .item-title {
                color: white;
                font-weight: 500;
            }
        }
    }
}

/* Dark theme overrides if needed */
.dark {
    .group-heading-container.active {
        .group-heading {
            background-color: lighten($darkCardBg, 6%);
        }
    }
    
    .sidebar-item {
        &.active {
            background-color: $secondaryColorDark;
        }
        &:hover {
            background-color: lighten($darkCardBg, 5%);
        }
        .sidebar-item-content {
            .item-dot { background-color: rgba($darkTextColor, 0.7); }
            .item-title { color: rgba($darkTextColor, 0.8); }
        }
    }
}

/* Animation */
.slide-enter-active, .slide-leave-active {
    transition: all 0.3s ease;
    max-height: 500px;
    opacity: 1;
    overflow: hidden;
}

.slide-enter, .slide-leave-to {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
}
</style>
